/* eslint-disable @next/next/no-img-element */
import { useEffect, useState } from 'react'
import { ModalV2 } from './Common/ModalV2'
import { useAuth } from 'util/auth'
import { StartFreeTrial } from 'pages/setup/billing'
import { useRouter } from 'next/router'
import { useProjectStore } from 'store/useProjectStore'
import { Button } from './Button'
import { IconLoader } from '@tabler/icons-react'
import { useSubmissionsCount } from 'util/db'

export const TrialRequired = ({ children }) => {
  const { user } = useAuth()
  const { data: submissionsCount } = useSubmissionsCount()

  const [modalOpen, setModalOpen] = useState(false)

  const { query } = useRouter()

  const project_id = query.project_id as string
  const submission_id = query.submission_id as string

  const just_started_trial = query.just_started_trial as string

  const redirectTo = `${process.env['NEXT_PUBLIC_BASE_URL']}/projects/${project_id}/upload/${submission_id}?just_started_trial=true`

  useEffect(() => {
    if (
      user &&
      !user.hasStartedTrial &&
      submissionsCount &&
      submissionsCount > 1
    ) {
      setModalOpen(true)
    }
  }, [user])

  if (user && user.hasStartedTrial) {
    return (
      <>
        {just_started_trial && <JustStartedTrialModal />}
        {children}
      </>
    )
  }

  return (
    <>
      <ModalV2
        preventClose
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        className="flex w-screen sm:max-h-[500px] md:max-w-3xl"
        includePadding={false}
      >
        <div className="grid grid-cols-3">
          <div className="col-span-1 hidden h-full md:block">
            <ImageWithLoading
              src="/pro.png"
              className="rounded-l-lg object-cover sm:max-h-[500px]"
              alt="Outcast Logo"
            />
          </div>
          <div className="col-span-3 flex flex-1 flex-col items-center justify-center px-4 py-4 md:col-span-2">
            <StartFreeTrial />
          </div>
        </div>
      </ModalV2>
      {children}
    </>
  )
}

export const JustStartedTrialModal = () => {
  const [modalOpen, setModalOpen] = useState(true)

  const { setShowConfetti } = useProjectStore()
  const router = useRouter()

  useEffect(() => {
    setShowConfetti(true)
  }, [])

  const handleClose = () => {
    setModalOpen(false)

    const currentUrl = new URL(window.location.href)
    const searchParams = currentUrl.searchParams

    searchParams.delete('just_started_trial')

    const updatedUrl = `${currentUrl.pathname}${
      searchParams.toString() ? '?' + searchParams.toString() : ''
    }`
    router.push(updatedUrl)
  }

  return (
    <ModalV2
      open={modalOpen}
      onClose={handleClose}
      className="flex max-h-[500px] w-screen md:max-w-lg"
      includePadding={false}
    >
      <div className="flex">
        <div className="hidden h-full flex-1 md:block">
          <ImageWithLoading
            src="/trial-started.png"
            className="max-h-[500px] rounded-l-lg object-cover"
            alt="Outcast Logo"
          />
        </div>
        <div className="flex flex-1 flex-col justify-center px-8 py-4">
          <h1 className="mb-2 whitespace-nowrap text-xl font-bold">
            Free Trial Started!
          </h1>
          <p className="prose text-sm text-slate-500">
            <span className="text-sm font-bold text-slate-500">
              {
                "We've added 60 minutes of uploads to your account to get you started."
              }
            </span>
          </p>

          <Button className="mt-8 w-full" onClick={handleClose}>
            Access Content!
          </Button>
          <p className="mt-2 text-xs text-slate-500">
            * Cancel anytime in your account settings.
          </p>
        </div>
      </div>
    </ModalV2>
  )
}

export const ImageWithLoading = ({
  src,
  alt,
  className,
}: {
  src: string
  alt: string
  className?: string
}) => {
  const [loading, setLoading] = useState(true)

  return (
    <div className={`relative`}>
      {loading && (
        <div
          className={`absolute inset-0 flex items-center justify-center bg-gray-100 ${className}`}
        >
          <IconLoader className="animate-spin" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`h-auto w-full transition-opacity duration-300 ${
          loading ? 'opacity-0' : 'opacity-100'
        } ${className}`}
        onLoad={() => setLoading(false)}
        onError={() => setLoading(false)}
      />
    </div>
  )
}
